import React, { useState } from 'react';

// XP options with time estimates:
// 100 XP: ~10 minutes, 1000 XP: ~1 hour
const XP_OPTIONS = [
  { value: 100, label: '100 XP', description: 'xp100Description' },
  { value: 200, label: '200 XP', description: 'xp200Description' },
  { value: 500, label: '500 XP', description: 'xp500Description' },
  { value: 750, label: '750 XP', description: 'xp750Description' },
  { value: 1000, label: '1000 XP', description: 'xp1000Description' }
];

const DEFAULT_MAX_XP = Infinity;

const BasicModeInput = ({ targetXp, onChange, t, maxXp = DEFAULT_MAX_XP, onUpgrade }) => {
  // Filter options based on maxXp (for free users, maxXp = 200)
  const availableOptions = XP_OPTIONS.filter(option => option.value <= maxXp);
  const lockedOptions = XP_OPTIONS.filter(option => option.value > maxXp);
  // User is "free" if they have a finite limit (subscribed users have Infinity)
  const isFreeUser = isFinite(maxXp);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  return (
    <div className="mb-4 pb-4 border-bottom" data-testid="basic-mode-section">
      <label className="form-label fw-semibold">
        <i className="bi bi-trophy text-warning me-1"></i>
        {t('xpTarget')}
        {isFreeUser && (
          <span
            className="badge bg-secondary ms-2"
            style={{ fontSize: '0.6em', verticalAlign: 'middle', cursor: 'pointer' }}
            onClick={() => setShowUpgradeModal(true)}
            data-testid="max-xp-badge"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && setShowUpgradeModal(true)}
          >
            <i className="bi bi-lock-fill me-1"></i>
            Max {maxXp} XP
          </span>
        )}
      </label>
      <p className="text-muted small mb-3">
        {t('selectDailyTarget')}
      </p>

      {/* XP option cards */}
      <div className="d-flex flex-column gap-2">
        {availableOptions.map((option) => (
          <div
            key={option.value}
            className={`card cursor-pointer ${
              targetXp === option.value ? 'border-primary bg-primary bg-opacity-10' : ''
            }`}
            style={{ cursor: 'pointer' }}
            onClick={() => onChange(option.value)}
            data-testid={`xp-option-${option.value}`}
          >
            <div className="card-body py-3 d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-3">
                <div
                  style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    border: `2px solid ${targetXp === option.value ? '#0d6efd' : '#dee2e6'}`,
                    backgroundColor: targetXp === option.value ? '#0d6efd' : 'transparent'
                  }}
                />
                <div>
                  <div className="fw-bold">{option.label}</div>
                  <div className="text-muted small">{t(option.description)}</div>
                </div>
              </div>
            </div>
          </div>
        ))}
        {/* Locked options for free users */}
        {lockedOptions.map((option) => (
          <div
            key={option.value}
            className="card"
            style={{ cursor: 'not-allowed', opacity: 0.5, filter: 'grayscale(100%)' }}
            data-testid={`xp-option-${option.value}-locked`}
          >
            <div className="card-body py-3 d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-3">
                <div
                  style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    border: '2px solid #dee2e6',
                    backgroundColor: 'transparent'
                  }}
                />
                <div>
                  <div className="fw-bold">{option.label}</div>
                  <div className="text-muted small">{t(option.description)}</div>
                </div>
              </div>
              <span className="badge bg-secondary">
                <i className="bi bi-lock-fill me-1"></i>
                Pro
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={() => setShowUpgradeModal(false)}
          data-testid="upgrade-modal"
        >
          <div
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-star-fill text-warning me-2"></i>
                  {t('upgradeModalTitle')}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowUpgradeModal(false)}
                  data-testid="upgrade-modal-close"
                ></button>
              </div>
              <div className="modal-body">
                <p className="mb-0">
                  {t('upgradeModalDescription')}
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowUpgradeModal(false)}
                >
                  {t('upgradeModalClose')}
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  data-testid="upgrade-button"
                  onClick={() => {
                    setShowUpgradeModal(false);
                    if (onUpgrade) {
                      onUpgrade();
                    }
                  }}
                >
                  <i className="bi bi-arrow-up-circle me-2"></i>
                  {t('upgradeModalUpgrade')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BasicModeInput;
